import React from 'react'
import { string, node, array, number, shape } from 'prop-types'

import ResponsiveImage from '../ResponsiveImage'
import ResponsiveMedia from '../ResponsiveMedia'
import Type from '../Type'
import Grid from '../Grid'
import Hide from '../Hide'
import Stack from '../Stack'
import Icon from '../Icon'
import ProfileTag from './component/ProfileTag'
import Prose from '@/component/Primitive/Prose'

import cx from 'classnames'
import styles from './ProfileHeaderNew.module.scss'

const ProfileHeader = ({
  heading,
  subHeading,
  description,
  meta,
  children,
  src,
  srcSet,
  imageRatio
}) => (
  <div className={styles.ProfileHeader}>
    <Grid flex gutter="default" middle>
      <Grid.Item width={{ d: 2 / 3 }}>
        <Stack gap="medium">
          <div className={styles.MainHeader}>
            <Hide at="desktop">
              <div className={styles.Image}>
                <ResponsiveMedia ratio={imageRatio}>
                  <ResponsiveImage
                    loading="eager"
                    alt="Profile Picture"
                    src={src}
                    srcSet={srcSet}
                    sizes={[
                      '(min-width:1150px 220px',
                      '(min-width:960px) 18vw',
                      '100vw'
                    ]}
                  />
                </ResponsiveMedia>
              </div>
            </Hide>
            <Hide below="desktop">
              <div className={cx(styles.Image)}>
                <ResponsiveMedia ratio={imageRatio}>
                  <ResponsiveImage
                    loading="eager"
                    alt="Profile Picture"
                    src={src}
                    srcSet={srcSet}
                    sizes={[
                      '(min-width:1150px 220px',
                      '(min-width:960px) 18vw',
                      '100vw'
                    ]}
                  />
                </ResponsiveMedia>
              </div>
            </Hide>
            <div className={styles.Top}>
              <div className={styles.Heading}>
                <Type
                  themed
                  as="h1"
                  size={['heading3', 'display1']}
                  weight="bold"
                >
                  {heading}
                </Type>
              </div>
              {subHeading && (
                <div className={styles.SubHeading}>
                  <Type
                    themed
                    as="h2"
                    size={['body2', 'heading6']}
                    weight="medium"
                  >
                    {subHeading}
                  </Type>
                </div>
              )}
            </div>
          </div>
          {meta && meta?.length ? (
            <div className={styles.MetaTable}>
              <dl className={cx(styles.TableItem, styles.title)}>
                <dt className={cx(styles.TableLabel, styles.highlight)}>
                  <Type weight="medium" size="body2">
                    {heading} Company Facts
                  </Type>
                  <Icon width="24" type="info" className={styles.InfoIcon} />
                </dt>
              </dl>
              {meta.map((item) => {
                if (!item.value) return null
                if (item.value.includes(';')) return null
                return (
                  <dl key={item.value} className={styles.TableItem}>
                    <dt className={styles.TableLabel}>
                      <Type weight="medium" size="body3">
                        {item.label}
                      </Type>
                    </dt>
                    <dd className={styles.TableValue}>
                      <Type size="body3">{item.value}</Type>
                    </dd>
                  </dl>
                )
              })}
            </div>
          ) : null}
          {description && (
            <>
              <Type weight="medium" size="body2" className={styles.Title}>
                Company Profile
              </Type>
              <Prose
                className={styles.Description}
                dangerousHtml={description}
              />
            </>
          )}
          {meta && (
            <div className={styles.Meta}>
              {meta.map((item) => {
                if (!item.value) return null
                if (item.value.includes(';')) {
                  const tags = item.value.split(';')
                  return (
                    <div className={styles.MetaList}>
                      <dl>
                        <dt className={styles.MetaTitle}>
                          <Type weight="medium" size="body2">
                            {item.label}
                          </Type>
                        </dt>
                      </dl>
                      <div className={styles.Meta}>
                        {tags.map((tag) => (
                          <ProfileTag
                            key={tag.value}
                            value={tag}
                            classNames={{ value: styles.ProfileTagKeywords }}
                          />
                        ))}
                      </div>
                    </div>
                  )
                }
                return null
              })}
            </div>
          )}
        </Stack>
      </Grid.Item>
      <Grid.Item width={{ d: 1 / 3 }}>
        <div className={styles.RightColumn}>{children}</div>
      </Grid.Item>
    </Grid>
  </div>
)

ProfileHeader.propTypes = {
  heading: string,
  subHeading: string,
  description: string,
  children: node,
  src: string,
  srcSet: array,
  imageRatio: number,
  meta: shape({
    label: string,
    value: string
  })
}

export default ProfileHeader
